/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import {
  Grid,
  Form,
  DateInput,
  Button,
  Icon,
  Modal,
  Tooltip,
} from '@mairistem/composants';

import { ChampLieu } from '@/components/Common';
import { API_DATE_FORMAT } from '@/constants';
import {
  createTimeInputHandler,
  buildEvenementNaissanceUpdatePayload,
  parseAdresseNaissance,
} from '@/utils';

interface ModalEvenementNaissanceProps {
  open: boolean;
  /** Identifiant de la fiche (celui de l'acte), utilisé pour l'URL de `service.update`. */
  identifiant?: number;
  enfant?: any;
  /** Instance `service` de `useFiche` fournie par `ActeFicheContainer` (celle de la lecture). */
  service?: { update: (data: Record<string, any>) => Promise<any> };
  onClose: () => void;
  onSaved: (enfant: any) => void;
}

const CHAMPS_EDITABLES = ['naissanceDate', 'naissanceHeure', 'naissanceMinute'];

const ModalEvenementNaissance = (props: ModalEvenementNaissanceProps) => {
  const { open, identifiant, enfant, service, onClose, onSaved } = props;

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);
  const [attempted, setAttempted] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setData({
      ..._.pick(enfant, CHAMPS_EDITABLES),
      adresseNaissance: parseAdresseNaissance(enfant),
    });
    setAttempted(false);
  }, [open, enfant]);

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      setData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleTimeInput = React.useMemo(
    () => createTimeInputHandler(handleChange),
    [handleChange],
  );

  const handleDateChange = React.useCallback(
    (date: Date | string | undefined) => {
      setData((prev) => ({
        ...prev,
        naissanceDate: _.isNil(date)
          ? null
          : dayjs(date).format(API_DATE_FORMAT),
      }));
    },
    [],
  );

  const errors = React.useMemo(() => {
    const e: Record<string, string> = {};
    if (_.isEmpty(data?.naissanceDate)) {
      e.naissanceDate = 'La date de naissance est obligatoire';
    }
    if (_.isEmpty(data?.adresseNaissance?.libelleCommune)) {
      e.adresseNaissance = 'Le lieu de naissance est obligatoire';
    }
    return e;
  }, [data]);

  const displayedErrors = attempted ? errors : {};

  const handleSave = React.useCallback(() => {
    setAttempted(true);
    if (!_.isEmpty(errors)) {
      return;
    }

    const payload = buildEvenementNaissanceUpdatePayload(
      identifiant as number,
      enfant?.identifiant,
      data,
    );

    setSaving(true);
    service
      ?.update(payload)
      .then(() => {
        onSaved({ ...enfant, ...payload.enfant });
        onClose();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement de l'événement",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [errors, service, identifiant, enfant, data, onSaved, onClose]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header content="Modifier l'événement" />
      <Modal.Content>
        {/* @ts-ignore */}
        <Form>
          <Grid>
            <Grid.Row style={{ marginLeft: '1em', paddingBottom: 0 }}>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field error={!_.isNil(displayedErrors.naissanceDate)}>
                  <label>Né le</label>
                  <Tooltip
                    trigger={
                      <DateInput
                        value={
                          data?.naissanceDate
                            ? dayjs(data.naissanceDate).toDate()
                            : undefined
                        }
                        onChange={handleDateChange}
                      />
                    }
                    type={displayedErrors.naissanceDate ? 'error' : ''}
                    message={displayedErrors.naissanceDate}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <Form.Group style={{ alignItems: 'flex-end' }}>
                  <Form.Input
                    label="à"
                    placeholder="hh"
                    name="naissanceHeure"
                    style={{ width: 48 }}
                    value={
                      !_.isNil(data?.naissanceHeure)
                        ? String(data.naissanceHeure).padStart(2, '0')
                        : ''
                    }
                    onChange={handleTimeInput}
                  />
                  <span style={{ alignSelf: 'flex-end', lineHeight: '38px' }}>
                    :
                  </span>
                  <Form.Input
                    placeholder="mm"
                    name="naissanceMinute"
                    style={{ width: 48 }}
                    value={
                      !_.isNil(data?.naissanceMinute)
                        ? String(data.naissanceMinute).padStart(2, '0')
                        : ''
                    }
                    onChange={handleTimeInput}
                  />
                </Form.Group>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16}>
                <ChampLieu
                  data={data}
                  errors={{
                    adresseNaissance: displayedErrors.adresseNaissance,
                  }}
                  onChange={handleChange}
                  dateValidite={
                    data?.naissanceDate
                      ? dayjs(data.naissanceDate).format(API_DATE_FORMAT)
                      : undefined
                  }
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          loading={saving}
          disabled={saving}
          onClick={handleSave}
        >
          <Icon name="check" />
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalEvenementNaissance;
